<template>
  <v-card id="bid-default-costs-form">
    <v-card-title class="primary white--text">
      <span>{{ $t('defaultBidCosts') }}</span>
      <v-spacer/>
      <BaseDialogActions
      hideRefresh
      hideFullscreen
      customCancel
      elementId="bid-default-costs-dialog-actions"
      @close="$emit('close')"/>
    </v-card-title>
    <v-card-text class="mt-4">
      <v-row dense>
        <v-col cols="12">
          <v-chip class="mb-2 mr-2">
            <span class="font-weight-bold">{{ bidName }}</span>
          </v-chip>
          <v-chip v-if="distanceText" class="mb-2 mr-2">
            <span class="font-weight-bold">{{ distanceText }}</span>
          </v-chip>
        </v-col>
        <v-col cols="12" class="caption text--secondary pb-2">
          {{ $t('defaultBidCostsHint') }}
        </v-col>
      </v-row>

      <FormWrapper
      formRef="bidDefaultCostsForm"
      :buttonText="guided ? $t('next') : $t('done')"
      :disabled="pending && !complete"
      @submit="finish">
        <v-card outlined class="mb-4">
          <v-card-text class="pt-2 px-3 pb-2">
            <BidCostComponentList
            :costComponents="listedRows"
            :editingId="editTarget ? editTarget.bidCostTypeId : null"
            hideTotal
            @edit="beginEdit"
            @delete="remove"/>

            <v-row v-if="availableCostTypes.length > 0" dense no-gutters align="center" :key="entryKey">
              <v-col cols="7" class="pr-2" @keydown.enter.prevent>
                <v-autocomplete
                ref="bidCostType"
                :items="availableCostTypes"
                item-text="name"
                item-value="bidCostTypeId"
                color="black"
                item-color="primary"
                v-model="bidCostTypeId"
                :readonly="editing"
                autofocus
                id="bid-default-cost-type-autocomplete"/>
              </v-col>
              <v-col cols="3" @keydown.enter.prevent="save">
                <MoneyTextField
                ref="costRate"
                :initialValue="rate"
                :label="$t('rate')"
                @val-changed="rate = $event"/>
              </v-col>
              <v-col cols="2" class="text-right">
                <Icon
                :icon="editing ? 'mdi-check' : 'mdi-plus'"
                iconColor="success"
                tabindex="0"
                :disabled="!complete"
                :tooltipText="editing ? $t('save') : $t('addCostComponent')"
                @icon-clicked="save"/>
                <Icon
                v-if="editing"
                icon="mdi-close"
                iconColor="error"
                tabindex="0"
                :tooltipText="$t('cancel')"
                @icon-clicked="cancelEdit"/>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'BidDefaultCostsDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue'),
    BidCostComponentList: () => import('@/components/bid/BidCostComponentList.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    costComponents: {
      type: Array,
      required: true
    },
    bidName: {
      type: String,
      default: ''
    },
    distance: {
      type: [String, Number],
      default: undefined
    },
    guided: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    rows: [],
    bidCostTypeId: undefined,
    rate: undefined,
    editTarget: null,
    autoTypeId: undefined,
    entryKey: 0
  }),

  created () {
    this.rows = this.costComponents.map(c => ({ ...c }))
    this.selectRemainingCostType()
  },

  mounted () {
    this.focusCostEntry()
  },

  computed: {
    ...mapGetters('tract', ['allBidCostTypes']),

    editing () {
      return !!this.editTarget
    },

    complete () {
      return typeof this.bidCostTypeId === 'number' && typeof this.rate === 'number' && this.rate > 0
    },

    pending () {
      return this.editing || (typeof this.bidCostTypeId === 'number' && this.bidCostTypeId !== this.autoTypeId)
    },

    effectiveRows () {
      if (!this.complete) return this.rows
      const row = {
        bidCostTypeId: this.bidCostTypeId,
        bidCostTypeName: this.allBidCostTypes.find(bct => bct.bidCostTypeId === this.bidCostTypeId)?.name,
        rate: this.rate
      }
      return this.editing
        ? this.rows.map(r => r.bidCostTypeId === this.editTarget.bidCostTypeId ? row : r)
        : [...this.rows, row]
    },

    listedRows () {
      return this.editing ? this.effectiveRows : this.rows
    },

    availableCostTypes () {
      return this.allBidCostTypes.filter(bct =>
        bct.bidCostTypeId === this.editTarget?.bidCostTypeId ||
        !this.rows.some(r => r.bidCostTypeId === bct.bidCostTypeId))
    },

    distanceText () {
      if (this.distance === null || this.distance === undefined || this.distance === '') return undefined
      return this.$t('milesShort', { distance: this.distance })
    }
  },

  methods: {
    beginEdit (row) {
      this.editTarget = row
      this.bidCostTypeId = row.bidCostTypeId
      this.rate = row.rate
      this.entryKey++
      this.focusCostEntry()
    },

    cancelEdit () {
      this.editTarget = null
      this.resetEntry()
    },

    save () {
      if (!this.complete) return
      const adding = !this.editing
      this.rows = this.effectiveRows
      this.editTarget = null
      this.$emit('saved', this.rows)
      if (adding && this.guided && this.availableCostTypes.length === 0) {
        this.$emit('close')
        return
      }
      this.resetEntry()
    },

    finish () {
      if (this.complete) {
        this.rows = this.effectiveRows
        this.$emit('saved', this.rows)
      }
      this.$emit('close')
    },

    remove (row) {
      const resetEntry = this.editTarget?.bidCostTypeId === row.bidCostTypeId || (!this.pending && !(this.rate > 0))
      this.rows = this.rows.filter(r => r.bidCostTypeId !== row.bidCostTypeId)
      this.$emit('saved', this.rows)
      if (resetEntry) this.cancelEdit()
    },

    resetEntry () {
      this.selectRemainingCostType()
      this.rate = undefined
      this.entryKey++
      this.focusCostEntry()
    },

    selectRemainingCostType () {
      this.autoTypeId = this.availableCostTypes.length === 1 ? this.availableCostTypes[0].bidCostTypeId : undefined
      this.bidCostTypeId = this.autoTypeId
    },

    focusCostEntry () {
      this.$nextTick(() => window.requestAnimationFrame(() => {
        const autoSelected = this.autoTypeId !== undefined && this.bidCostTypeId === this.autoTypeId
        const field = this.editing || autoSelected ? this.$refs.costRate?.$children[0] : this.$refs.bidCostType
        if (field) field.focus()
      }))
    }
  }
}
</script>
